'use client';

import { useCallback, useEffect, useRef } from 'react';

import Cookies from 'js-cookie';

import useUser from '@/utils/hooks/swr/useUser';

import {
    getLocalStorageLanguage,
    setLocalStorageLanguage
} from '@/utils/localStorage/languageLocalStorage';

import type { PlatformLanguagesType } from '@/types/userType';

import { LOCALE_COOKIE_KEY } from '../../../middleware';

/**
 * Set locale cookie using js-cookie
 */
const setLocaleCookie = (locale: string) => {
    Cookies.set(LOCALE_COOKIE_KEY, locale, {
        path: '/',
        expires: 365,
        sameSite: 'lax'
    });
};

/**
 * Get current locale from cookie using js-cookie
 */
const getLocaleCookie = (): string | undefined => {
    return Cookies.get(LOCALE_COOKIE_KEY);
};

const useLanguage = () => {
    // Set language via cookie and reload page to get new translations
    const setLanguage = useCallback((locale: string) => {
        const currentCookie = getLocaleCookie();
        if (currentCookie !== locale) {
            setLocaleCookie(locale);
            // Reload page to load new messages via _app.tsx
            window.location.reload();
        }
    }, []);

    // Sync language from user settings (only once on mount)
    const useSetLanguage = () => {
        const { user } = useUser();
        const platformLanguage = user?.settings?.platform_language;
        const hasInitialized = useRef(false);

        useEffect(() => {
            if (hasInitialized.current) return;

            const localStorageLanguage = getLocalStorageLanguage();
            const cookieLanguage = getLocaleCookie();

            // Seed localStorage from cookie if present, otherwise default to 'en'
            if (!localStorageLanguage) {
                if (cookieLanguage) {
                    setLocalStorageLanguage(cookieLanguage as PlatformLanguagesType);
                } else {
                    setLocalStorageLanguage('en');
                }
            }

            // Sync cookie with user's platform language preference and reload
            if (platformLanguage && platformLanguage !== cookieLanguage) {
                hasInitialized.current = true;
                setLocalStorageLanguage(platformLanguage);
                setLocaleCookie(platformLanguage);
                // Reload to apply the new language
                window.location.reload();
            }
        }, [platformLanguage]);
    };

    const setLanguageLocalStorage = useCallback(
        (platformLanguage: PlatformLanguagesType) => {
            const localStorageLanguage = getLocalStorageLanguage();

            if (!localStorageLanguage) {
                setLocalStorageLanguage(platformLanguage);
            }

            if (platformLanguage !== localStorageLanguage) {
                setLocalStorageLanguage(platformLanguage);
                setLanguage(platformLanguage);
            }
        },
        [setLanguage]
    );

    return { useSetLanguage, setLanguageLocalStorage, getLocaleCookie };
};

export default useLanguage;
